import React from 'react'
import { Field, useFormikContext } from 'formik'
import moment from 'moment'

import { CheckboxGroup, Select, NumberField } from '@/components/forms/formik'
import { FormField } from '@/components/forms/FormField'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'

const CALLING_DAY_OPTIONS = [
  { value: 0, text: 'Sunday' },
  { value: 1, text: 'Monday' },
  { value: 2, text: 'Tuesday' },
  { value: 3, text: 'Wednesday' },
  { value: 4, text: 'Thursday' },
  { value: 5, text: 'Friday' },
  { value: 6, text: 'Saturday' },
]

export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern (America/New_York)' },
  { value: 'America/Chicago', label: 'Central (America/Chicago)' },
  { value: 'America/Denver', label: 'Mountain (America/Denver)' },
  { value: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { value: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
]

export const CALLING_HOUR_START_OPTIONS = [
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '19:00', label: '7:00 PM' },
  { value: '20:00', label: '8:00 PM' },
]

export const CALLING_HOUR_END_OPTIONS = [
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '19:00', label: '7:00 PM' },
  { value: '20:00', label: '8:00 PM' },
  { value: '21:00', label: '9:00 PM' },
]

export const CALLING_HOUR_OPTIONS = [
  ...CALLING_HOUR_START_OPTIONS,
  { value: '21:00', label: '9:00 PM' },
]

export const MIN_CALL_INTERVAL_HOURS = 24

export const DEFAULT_CALL_INTERVAL_HOURS = 24

export const MIN_CALLING_HOUR = '08:00'
export const MAX_CALLING_HOUR = '21:00'

export const ScheduleRetryPage = () => {
  const { values, errors, touched, setFieldValue, setFieldTouched } = useFormikContext()
  const dateError =
    (errors.start_date && touched.start_date) || (errors.end_date && touched.end_date)

  const handleDateChange = ({ selection }) => {
    if (selection.startDate) {
      setFieldValue('start_date', moment(selection.startDate).format('YYYY-MM-DD'))
      setFieldTouched('start_date', true, false)
    }
    if (selection.endDate) {
      setFieldValue('end_date', moment(selection.endDate).format('YYYY-MM-DD'))
      setFieldTouched('end_date', true, false)
    }
  }

  return (
    <div className="field-stack">
      <h3>Schedule & Retry</h3>

      <Field
        name="calling_days"
        label="Calling Days"
        options={CALLING_DAY_OPTIONS}
        component={CheckboxGroup}
        required
      />

      <div className="field-row">
        <Field
          name="calling_hours_start"
          label="Calling Hours Start"
          placeholder="Select start time"
          options={CALLING_HOUR_START_OPTIONS}
          component={Select}
          required
          touchOnChange
          data-testid="calling-hours-start-select"
        />
        <Field
          name="calling_hours_end"
          label="Calling Hours End"
          placeholder="Select end time"
          options={CALLING_HOUR_END_OPTIONS}
          component={Select}
          required
          touchOnChange
          data-testid="calling-hours-end-select"
        />
      </div>

      <div className="field-row">
        <Field
          name="timezone"
          label="Timezone"
          options={TIMEZONE_OPTIONS}
          component={Select}
          touchOnChange
          data-testid="timezone-select"
        />
        <Field
          name="transfer_timezone"
          label="Transfer Timezone"
          options={TIMEZONE_OPTIONS}
          component={Select}
          touchOnChange
          data-testid="transfer-timezone-select"
        />
      </div>

      <FormField required error={Boolean(dateError)}>
        <label>Campaign Dates</label>
        <DateRangePickerReusable
          startDate={values.start_date || null}
          endDate={values.end_date || null}
          onChange={handleDateChange}
          error={dateError}
          allowFutureDates
          withSidebar={false}
          data-testid="campaign-date-range"
          saveOrCloseOnClickPreset
        />
      </FormField>

      <div className="field-row">
        <Field
          name="retries_no_answer"
          label="Retries (No Answer)"
          component={NumberField}
          min={0}
        />
        <Field
          name="retries_voicemail"
          label="Retries (Voicemail)"
          component={NumberField}
          min={0}
        />
        <Field
          name="max_concurrent_calls"
          label="Max Concurrent Calls"
          component={NumberField}
          min={1}
          max={40}
        />
      </div>

      <div className="field-row">
        <Field
          name="call_interval_hours"
          label="Interval Between Calls (Hours)"
          component={NumberField}
          required
          min={MIN_CALL_INTERVAL_HOURS}
          data-testid="call-interval-field"
        />
      </div>
    </div>
  )
}
